import { useEffect, useMemo, useState, useCallback } from "react";
import api from "@/utils/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { UserPlus, FileUp, Download, Search, Loader2, Mail } from "lucide-react";
import { EmailPreviewDialog } from "@/components/t4r/learners/EmailPreviewDialog";
import { LearnerEmailsBanner } from "@/components/LearnerEmailsSwitch";
import useDocumentTitle from "@/hooks/useDocumentTitle";
import { useBranding } from "@/contexts/BrandingContext";
import { ManageLearnerDialog } from "@/components/t4r/ManageLearnerDialog";
import { LearnerProgressDialog } from "@/components/t4r/LearnerProgressDialog";
import { LmsImportDialog } from "@/components/t4r/LmsImportDialog";
import { LearnerTable, sortRows } from "@/components/t4r/learners/LearnerTable";
import { BulkBar } from "@/components/t4r/learners/BulkBar";
import { InviteLearnerDialog } from "@/components/t4r/learners/InviteLearnerDialog";
import { exportLearnersCsv } from "@/components/t4r/learners/exportCsv";
import { useSelection, useSort, matchesSearch } from "@/components/t4r/learners/useLearnerList";

const Kpi = ({ label, value, active, onClick, testId }) => (
  <button onClick={onClick} className={`rounded-xl border px-4 py-3 text-left transition-colors ${active ? "border-green-500 bg-green-50" : "border-border bg-white hover:border-green-200"}`} data-testid={testId}>
    <p className="font-heading text-2xl font-bold text-slate-900">{value}</p><p className="text-xs text-slate-500">{label}</p>
  </button>
);

const selectCls = "h-9 rounded-md border border-border bg-white px-2 text-sm";

export default function T4RLearnersPage() {
  const { lms_name } = useBranding();
  useDocumentTitle("Learners", "LMS learners and invites", lms_name);
  const [learners, setLearners] = useState(null);
  const [courses, setCourses] = useState([]);
  const [q, setQ] = useState("");
  const [course, setCourse] = useState("");
  const [status, setStatus] = useState("");
  const [inviting, setInviting] = useState(false);
  const [managing, setManaging] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [importing, setImporting] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const { selected, toggle, setMany, clear } = useSelection();
  const { sort, onSort } = useSort();

  const load = useCallback(async () => {
    const { data } = await api.get("/lms/admin/learners/overview"); setLearners(data.learners || []); return data.learners || [];
  }, []);
  useEffect(() => { load(); api.get("/lms/admin/courses").then(({ data }) => setCourses(data.courses || [])).catch(() => {}); }, [load]);

  const rows = useMemo(() => sortRows((learners || []).filter((l) => matchesSearch(l, q) && (!course || l.course_ids.includes(course)) && (!status || l.status === status)), sort), [learners, q, course, status, sort]);
  const all = learners || [];
  const count = (s) => all.filter((l) => l.status === s).length;

  return (
    <div className="space-y-5 pb-16" data-testid="t4r-learners-page">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="font-heading text-2xl font-bold tracking-tight">Learners</h1>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" className="gap-1" onClick={() => setEmailOpen(true)} data-testid="welcome-email-btn"><Mail size={12} /> Welcome email</Button>
          <Button size="sm" variant="outline" className="gap-1" onClick={() => exportLearnersCsv(rows)} disabled={!rows.length} data-testid="export-csv-btn"><Download size={12} /> Export CSV ({rows.length})</Button>
          <Button size="sm" variant="outline" className="gap-1" onClick={() => setImporting(true)} data-testid="import-csv-btn"><FileUp size={12} /> Import CSV</Button>
          <Button size="sm" className="gap-1 bg-green-700 text-white hover:bg-green-800" onClick={() => setInviting(true)} data-testid="invite-btn"><UserPlus size={12} /> Invite learner</Button>
        </div>
      </div>
      <LearnerEmailsBanner />
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Kpi label="All learners" value={all.length} active={!status} onClick={() => setStatus("")} testId="kpi-all" />
        <Kpi label="Active" value={count("active")} active={status === "active"} onClick={() => setStatus("active")} testId="kpi-active" />
        <Kpi label="Invited — not signed in yet" value={count("invited")} active={status === "invited"} onClick={() => setStatus("invited")} testId="kpi-invited" />
        <Kpi label="Suspended" value={count("archived")} active={status === "archived"} onClick={() => setStatus("archived")} testId="kpi-suspended" />
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[240px] flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or email" className="pl-8" data-testid="learner-search" />
        </div>
        <select value={course} onChange={(e) => setCourse(e.target.value)} className={selectCls} data-testid="filter-course">
          <option value="">All courses</option>{courses.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className={selectCls} data-testid="filter-status">
          <option value="">Any status</option><option value="active">Active</option><option value="invited">Invited</option><option value="archived">Suspended</option>
        </select>
        <span className="text-xs text-muted-foreground" data-testid="learner-count">Showing {rows.length} of {all.length}</span>
      </div>
      {learners === null ? <div className="flex justify-center py-16"><Loader2 className="animate-spin text-slate-400" /></div> : (
        <LearnerTable rows={rows} selected={selected} onToggle={toggle} onToggleAll={(on) => setMany(rows.map((r) => r.id), on)} sort={sort} onSort={onSort} onOpen={setViewing} onManage={setManaging} />
      )}
      <BulkBar ids={[...selected]} courses={courses} onDone={() => { clear(); load(); }} onClear={clear} />
      <LearnerProgressDialog learnerId={viewing} onClose={() => setViewing(null)} />
      <EmailPreviewDialog open={emailOpen} onClose={() => setEmailOpen(false)} name="Jane Doe" courseIds={courses.slice(0, 1).map((c) => c.id)} />
      <LmsImportDialog open={importing} onClose={() => setImporting(false)} onImported={load} />
      <InviteLearnerDialog open={inviting} onClose={() => setInviting(false)} courses={courses} onInvited={load} />
      <ManageLearnerDialog learner={managing} courses={courses} onClose={() => setManaging(null)} onChanged={async () => { const list = await load(); setManaging((m) => m && list.find((x) => x.id === m.id)); }} />
    </div>
  );
}
